import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { EmergencyContact } from '@/app/modules/uploadStips/components/References/references.services';
import type { OcrDetail } from '@/app/modules/uploadStips/types';
import type { RootState } from '@/store/index';

export interface FileMd5Entry {
  md5: string;
  submitted: boolean;
}

export interface UploadState {
  fileMd5Map: Record<string, FileMd5Entry[]>;
  filesOcrDetails: OcrDetail[];
  failedFileNames: string[];
  references: Record<string, Partial<EmergencyContact>>;
  showErrorModal: boolean;
  uploadedDocsCount: number;
}

const initialState: UploadState = {
  fileMd5Map: {},
  filesOcrDetails: [],
  failedFileNames: [],
  references: {
    'Emergency Contact 1': {},
    'Emergency Contact 2': {},
    'Emergency Contact 3': {},
  },
  showErrorModal: false,
  uploadedDocsCount: 0,
};

export const uploadSlice = createSlice({
  name: 'upload',
  initialState,
  reducers: {
    setFilesOcrDetails: (state, action: PayloadAction<any[]>) => {
      state.filesOcrDetails = Array.isArray(action.payload) ? action.payload : [];
    },

    setFailedFileNames: (state, action: PayloadAction<string[]>) => {
      state.failedFileNames = Array.isArray(action.payload) ? action.payload : [];
    },

    setFileMd5: (
      state,
      action: PayloadAction<{
        stipRequestItemId: string;
        md5: string;
      }>
    ) => {
      const { stipRequestItemId, md5 } = action.payload;

      const existing = state.fileMd5Map[stipRequestItemId] || [];

      const alreadyExists = existing.some((entry) => entry.md5 === md5);

      if (!alreadyExists) {
        state.fileMd5Map[stipRequestItemId] = [...existing, { md5, submitted: false }];
      }
    },

    markFileSubmitted: (
      state,
      action: PayloadAction<{
        stipRequestItemId: string;
        md5: string;
      }>
    ) => {
      const { stipRequestItemId, md5 } = action.payload;

      const existing = state.fileMd5Map[stipRequestItemId] || [];

      state.fileMd5Map[stipRequestItemId] = existing.map((entry) => (entry.md5 === md5 ? { ...entry, submitted: true } : entry));
    },
    setReferences: (state, action: PayloadAction<Record<string, Partial<EmergencyContact>>>) => {
      state.references = { ...state.references, ...action.payload };
    },
    updateReference: (state, action: PayloadAction<{ key: string; reference: Partial<EmergencyContact> }>) => {
      const { key, reference } = action.payload;
      if (state.references[key]) {
        state.references[key] = { ...state.references[key], ...reference };
      }
    },
    setUploadedDocsCount: (state, action: PayloadAction<number>) => {
      state.uploadedDocsCount = action.payload;
    },
    toggleErrorModal: (state, action: PayloadAction<boolean>) => {
      state.showErrorModal = action.payload;
    },
    resetDocumentsState: () => initialState,
  },
});

export const { setFilesOcrDetails, setFailedFileNames, setFileMd5, markFileSubmitted, setReferences, updateReference, toggleErrorModal, resetDocumentsState, setUploadedDocsCount } = uploadSlice.actions;

export const selectReferences = (state: RootState) => state.upload.references;

export const selectShowErrorModal = (state: RootState) =>
  state.upload.showErrorModal;

export const selectUploadedDocsCount = (state: RootState) =>
  state.upload.uploadedDocsCount;

export default uploadSlice.reducer;
